import axios, { getData } from "../../../axios";
import { createSlice } from "@reduxjs/toolkit";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { decodeCargo } from "../../app/cargo-dialog/types";
import { Cargo } from "../cargos";
import { AppThunkAction, RootState } from "../../../redux-store";
import {
  splitRouteReqT,
  splitCargoReqT,
  unsplitRouteReqT,
  SplitCargoReq,
  SplitRouteReq,
  UnsplitRouteReq,
} from "dora-contracts";

const prefix = "data/split-cargo";

// TODO: MOVE ALL OF THIS IN APP, IT BELONGS IN APP, NOT DATA

type State = {
  cargos: Cargo[];
  pendingCount: number;
};

const initialState: State = {
  cargos: [],
  pendingCount: 0,
};

export const loadCargoForSplit = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo`,
  async (cargoId: string): Promise<Cargo> => {
    return axios.get(`/api/cargos/${cargoId}`).then(getData).then(decodeCargo);
  }
);

export const loadCargosForSplit =
  (cargoIds: string[]): AppThunkAction<Promise<void>> =>
  async (dispatch) => {
    await Promise.all(cargoIds.map((id) => dispatch(loadCargoForSplit(id))));
  };

export const splitCargo = createErrorReportingAsyncThunk(
  `${prefix}/split-cargo`,
  async (input: SplitCargoReq): Promise<void> => {
    await axios.post(
      `/api/cargos/${input.cargoId}/split`,
      splitCargoReqT.encode(input)
    );
  }
);

// Thrown when we stopped polling without ever seeing COMPLETED or FAILED - the
// job may still be running on the server, we just don't know its outcome.
// createAsyncThunk's `.unwrap()` re-throws a serialized plain object (only
// name/message/stack/code survive), not this class instance - callers must
// check `err.name === "JobPollTimeoutError"`, not `instanceof`.
export class JobPollTimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JobPollTimeoutError";
  }
}

const pollJobStatus = async (
  url: string,
  { intervalMs = 1500, timeoutMs = 180_000 } = {}
) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const { data } = await axios.get(url);
    if (data.status === "COMPLETED") return;
    if (data.status === "FAILED")
      throw new Error(data.error ?? "Job failed");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new JobPollTimeoutError("Job timed out after 3 minutes");
};

export const splitRoute = createErrorReportingAsyncThunk(
  `${prefix}/split-route`,
  async (input: SplitRouteReq): Promise<void> => {
    const { data } = await axios.post(
      `/api/routes/${input.routeId}/split`,
      splitRouteReqT.encode(input)
    );
    await pollJobStatus(`/api/routes/split-jobs/${data.jobId}`);
  }
);

export const unsplitCargo = createErrorReportingAsyncThunk(
  `${prefix}/unsplit`,
  async (cargoId: string): Promise<void> => {
    await axios.post(`/api/cargos/${cargoId}/unsplit`);
  }
);

export const unsplitRoute = createErrorReportingAsyncThunk(
  `${prefix}/unsplit-route`,
  async (input: UnsplitRouteReq & { routeId: string }): Promise<void> => {
    const { data } = await axios.post(
      `/api/routes/${input.routeId}/unsplit`,
      unsplitRouteReqT.encode({ cargosIds: input.cargosIds })
    );
    await pollJobStatus(`/api/routes/unsplit-jobs/${data.jobId}`);
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    clearSplitCargos: (state) => {
      state.cargos = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCargoForSplit.pending, (state) => {
        state.pendingCount += 1;
      })
      .addCase(loadCargoForSplit.fulfilled, (state, action) => {
        state.cargos.push(action.payload);
        state.pendingCount -= 1;
      })
      .addCase(loadCargoForSplit.rejected, (state) => {
        state.pendingCount -= 1;
      });
  },
});

export const { clearSplitCargos } = slice.actions;

export const selectSplitCargos = (state: RootState) =>
  state.data.splitCargo.cargos;

export const selectSplitCargosLoading = (state: RootState) =>
  state.data.splitCargo.pendingCount > 0;

export default slice.reducer;
